import { useContext, useEffect, useState } from "react";
import {
  Grid,
  TextInput,
  Select,
  Title,
  Text,
  ActionIcon,
  Flex,
  Group,
  Tooltip,
  Skeleton,
  Stack,
  Menu,
} from "@mantine/core";
import {
  IconSearch,
  IconArrowsSort,
  IconX,
  IconFolderPlus,
  IconSquareCheck,
  IconSquareOff,
  IconDotsVertical,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { FoldersContext } from "../contexts/FoldersContext";
import { AuthContext } from "../contexts/AuthContext";
import { useRootFolderLabel } from "../hooks/useRootFolderLabel";
import { useDebouncedCallback } from "@mantine/hooks";

const FoldersHeader = ({
  showTitle = true,
  showSearch = true,
  showSort = false,
  showMenu = true,
  onLocalSearchChange,
  checkAllFolderFilters,
  uncheckAllFolderFilters,
  "data-testid": dataTestId,
}) => {
  const {
    openCreate,
    canCreateFolder,
    setFolderToModify,
    sortBy,
    setSortBy,
    loading,
    folders,
    viewingFolder,
    handleExpandChecked,
    handleCollapseAll,
  } = useContext(FoldersContext);
  const { organisation, dataScopeOrgId } = useContext(AuthContext);
  const isContractor = organisation?.isContractor();
  const isViewingOwnOrg = String(organisation?.id) === String(dataScopeOrgId);
  const rootFolderLabel = useRootFolderLabel();
  const [searchValue, setSearchValue] = useState("");
  const iconSize = 18;
  const disabled = folders.length === 0;

  const showTopRow = showTitle || showMenu;
  const showBottomRow = showSearch || showSort;

  const handleCreateFolder = () => {
    setFolderToModify(folders[0]);
    openCreate();
  };

  const handleSearchChange = useDebouncedCallback((newQuery) => {
    if (onLocalSearchChange) {
      onLocalSearchChange(newQuery);
    }
  }, 400);

  const handleClearSearch = () => {
    setSearchValue("");
    if (onLocalSearchChange) {
      onLocalSearchChange("");
    }
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  useEffect(() => {
    if (viewingFolder) {
      setSearchValue("");
    }
  }, [viewingFolder]);

  if (loading) {
    return (
      <Grid gutter="md" align="center" py="md" px="sm">
        {showTopRow && (
          <>
            <Grid.Col span={6}>
              <Group gap="xs" ml={4}>
                <Skeleton circle width={20} height={20} />
                <Skeleton height={20} width={120} />
              </Group>
            </Grid.Col>
            <Grid.Col span={6} align="right">
              <Group align="center" justify="flex-end" gap={4}>
                <Skeleton circle width={20} height={20} />
                <Skeleton circle width={20} height={20} />
                <Skeleton circle width={20} height={20} />
                <Skeleton circle width={20} height={20} />
                <Skeleton circle width={20} height={20} />
              </Group>
            </Grid.Col>
          </>
        )}
        {showBottomRow && (
          <>
            <Grid.Col span={showSort ? 6 : 12}>
              <Skeleton height={30} radius="sm" />
            </Grid.Col>
            {showSort && (
              <Grid.Col span={6} align="right">
                <Skeleton height={30} radius="sm" width={160} />
              </Grid.Col>
            )}
          </>
        )}
      </Grid>
    );
  }

  return (
    <Stack gap="sm" py={17} px="sm">
      {showTopRow && (
        <Grid gutter="md" align="flex-start">
          <Grid.Col span="auto">
            {showTitle && (
              <Stack gap={0}>
                <Title order={5} lineClamp={1}>
                  {isContractor ? organisation.name : rootFolderLabel}
                </Title>
                {isContractor && !isViewingOwnOrg && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {rootFolderLabel}
                  </Text>
                )}
              </Stack>
            )}
          </Grid.Col>
          <Grid.Col span="content" style={{ textAlign: "right" }}>
            {showMenu && (
              <Flex align="center" justify="flex-end">
                {canCreateFolder && (
                  <Tooltip
                    label="Create folder"
                    position="top"
                    withArrow
                    transition="fade"
                  >
                    <ActionIcon
                      variant="subtle"
                      onClick={handleCreateFolder}
                      color="gray"
                    >
                      <IconFolderPlus size={iconSize} />
                    </ActionIcon>
                  </Tooltip>
                )}
                <Menu withArrow>
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      data-testid={
                        dataTestId
                          ? `${dataTestId}-menu-button`
                          : "folders-menu-button"
                      }
                      aria-label="Folders menu"
                    >
                      <IconDotsVertical size={iconSize} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {canCreateFolder && (
                      <>
                        <Menu.Item
                          leftSection={<IconFolderPlus size={iconSize} />}
                          onClick={handleCreateFolder}
                        >
                          Create folder
                        </Menu.Item>
                        <Menu.Divider />
                      </>
                    )}
                    <Menu.Item
                      leftSection={<IconSquareCheck size={iconSize} />}
                      onClick={checkAllFolderFilters}
                      disabled={disabled}
                      data-testid={
                        dataTestId
                          ? `${dataTestId}-select-all`
                          : "folders-select-all"
                      }
                    >
                      Select all
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSquareOff size={iconSize} />}
                      onClick={uncheckAllFolderFilters}
                      disabled={disabled}
                      data-testid={
                        dataTestId
                          ? `${dataTestId}-clear-selection`
                          : "folders-clear-selection"
                      }
                    >
                      Clear selection
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconChevronDown size={iconSize} />}
                      onClick={handleExpandChecked}
                      disabled={disabled}
                    >
                      Expand selected
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconChevronUp size={iconSize} />}
                      onClick={handleCollapseAll}
                      disabled={disabled}
                    >
                      Collapse all
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Flex>
            )}
          </Grid.Col>
        </Grid>
      )}
      {showBottomRow && (
        <Grid gutter="md" align="center">
          {showSearch && (
            <Grid.Col span="auto">
              <TextInput
                placeholder="Search"
                variant="default"
                value={searchValue}
                onChange={(event) => {
                  setSearchValue(event.target.value);
                  handleSearchChange(event.target.value);
                }}
                leftSection={<IconSearch size={16} />}
                size="xs"
                disabled={disabled}
                data-testid={
                  dataTestId
                    ? `${dataTestId}-search-input`
                    : "folders-search-input"
                }
                rightSection={
                  searchValue.trim().length > 0 && (
                    <ActionIcon
                      variant="transparent"
                      color="default"
                      onClick={handleClearSearch}
                      data-testid={
                        dataTestId
                          ? `${dataTestId}-clear-search`
                          : "folders-clear-search"
                      }
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  )
                }
              />
            </Grid.Col>
          )}
          {showSort && (
            <Grid.Col span="auto" align="right">
              <Select
                value={sortBy}
                variant="unstyled"
                onChange={handleSortChange}
                maw={160}
                disabled={disabled}
                data={[
                  { value: "alphabetical", label: "Alphabetical" },
                  { value: "createdDate", label: "Created Date" },
                ]}
                leftSection={<IconArrowsSort size={16} />}
                size="xs"
                mr="xs"
              />
            </Grid.Col>
          )}
        </Grid>
      )}
    </Stack>
  );
};

export default FoldersHeader;
